/* eslint-disable @typescript-eslint/no-floating-promises */
import { Box, Button, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, Tooltip, useToast, type ButtonProps } from "@chakra-ui/react"
import { IconArchive, IconCopy, IconEdit, IconEye, IconFileSpreadsheet, IconFileTypePdf, IconTrash } from "@tabler/icons-react"
import { useAtom, useAtomValue } from "jotai"
import React, { useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Paths } from "../../../common/commonConstants"
import useErrorToast from "../../../common/commonHooks/errorToast"
import { userDetailsAtom } from "../../../components/Menu/menu.store"
import { Colors } from "../../../theme/colors"
import { useDownloadFmpReportsMutation, useExportFmpToExcelMutation } from "../../../type-and-hooks"
import { ArchiveOrRestoreFMP } from "../common.fmp.actions/fmp.archive.main"
import { CopyFMP } from "../common.fmp.actions/fmp.copyfmp.main"
import { DeleteFMP } from "../common.fmp.actions/fmp.deletefmp.main"
import { formData } from "../common.fmp.hooks/formData.store"
import { pdfGeneratorAtom } from "../common.fmp.hooks/pdfGenerator.store"

const VIEW_ICON_SIZE = 24
const viewIconButtonStyle = (options?: { isDanger?: boolean; isDisabled?: boolean }): ButtonProps => {
  const { isDanger, isDisabled } = options ?? {}
  return {
    variant: "unstyled",
    isDisabled,
    minW: "40px",
    w: "40px",
    h: "40px",
    minH: "40px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bg: "transparent",
    color: "#4A4A4A",
    opacity: isDisabled ? 0.4 : 1,
    cursor: isDisabled ? "not-allowed" : "pointer",
    _hover: isDisabled ? {} : isDanger ? { bg: Colors.Errors[100], color: Colors.Errors.error } : { bg: Colors.Surface.s1, color: Colors.Secondary.c2 }
  }
}
const ViewActionDivider = (): JSX.Element => <Box w="0.5px" h="24px" bg={Colors.SecondaryPrimary[300]} mx="8px" flexShrink={0} />

interface FMPActionButtonsProps {
  fmpId?: number
  // Which page is rendering the icon row: "view" shows an "Edit FMP" icon,
  // "edit" swaps it for a "View FMP" icon that navigates the other way -
  // both would be self-referential on those pages, so only the useful one
  // is shown. "filter" is a neutral page (not "already" view or edit), so
  // it shows both View FMP and Edit FMP as separate icons.
  pageType?: "view" | "edit" | "filter"
}

export const FMPActionButtons: React.FC<FMPActionButtonsProps> = ({ fmpId, pageType = "view" }) => {
  const toast = useToast()
  const navigate = useNavigate()
  const [formDataAtom] = useAtom(formData)
  const [userDetails] = useAtom(userDetailsAtom)
  const pdfGenerator = useAtomValue(pdfGeneratorAtom)

  const [exportToFMP, { loading: PDFDownloadLoading, data, error }] = useDownloadFmpReportsMutation()
  const [exportToExcel, { loading: excelDownloadLoading, data: excelDownload, error: excelDownloadError }] = useExportFmpToExcelMutation()

  useErrorToast({ toast, error, errorMessage: "Error exporting FMP to PDF" })
  useErrorToast({ toast, error: excelDownloadError, errorMessage: "Error exporting FMP to Excel" })

  const handleDownload = useCallback(
    (downloadLink: string, downloadName: string) => {
      if (!downloadLink) {
        toast({
          title: "Error!. Please try again",
          description: "Failed to fetch Download link",
          status: "error",
          duration: 5000,
          isClosable: true
        })
        return
      }
      const link = document.createElement("a")
      link.href = downloadLink
      link.setAttribute("download", downloadName)
      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    [toast]
  )

  useEffect(() => {
    if (data?.downloadFMPReports) {
      handleDownload(data.downloadFMPReports, "downloadFMPReports.zip")
    }
  }, [data, handleDownload])

  useEffect(() => {
    if (excelDownload?.exportFMPToExcel) {
      handleDownload(excelDownload.exportFMPToExcel, "downloadExcelReports.zip")
    }
  }, [excelDownload, handleDownload])

  const handlePDFDownloadClick = useCallback(
    (option: number) => {
      try {
        const userId = userDetails?._id
        if (!userId) {
          toast({
            title: "Error",
            description: "User ID is missing. Please log in again.",
            status: "error",
            duration: 5000,
            isClosable: true
          })
          return
        }
        if (!fmpId) {
          toast({
            title: "Error",
            description: "FMP ID is missing. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true
          })
          return
        }
        const generatedPDF = option !== 2 ? pdfGenerator?.() ?? "" : ""
        if (option !== 2 && !pdfGenerator) {
          toast({
            title: "Error",
            description: "Unable to prepare PDF content. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true
          })
          return
        }
        exportToFMP({
          variables: {
            downloadFmpReportsId: fmpId,
            userId,
            html: generatedPDF,
            option
          }
        }).catch(err => {
          console.error("PDF download error:", err)
        })
      } catch (err) {
        console.error("PDF download click error:", err)
        toast({
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true
        })
      }
    },
    [fmpId, pdfGenerator, toast, userDetails?._id]
  )

  const handleExcelDownload = useCallback(() => {
    try {
      const userId = userDetails?._id
      if (!userId) {
        toast({
          title: "Error",
          description: "User ID is missing. Please log in again.",
          status: "error",
          duration: 5000,
          isClosable: true
        })
        return
      }
      if (!fmpId) {
        toast({
          title: "Error",
          description: "FMP ID is missing. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true
        })
        return
      }
      exportToExcel({
        variables: {
          exportFmpToExcelId: fmpId,
          userId
        }
      }).catch(err => {
        console.error("Excel export error:", err)
      })
    } catch (err) {
      console.error("Excel download click error:", err)
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true
      })
    }
  }, [fmpId, toast, userDetails?._id])

  const navigateToEditFMP = useCallback(() => {
    if (fmpId) {
      navigate(Paths.FMP.EDIT_FMP, { state: { fmpId: Number(fmpId) } })
    }
  }, [navigate, fmpId])

  const navigateToViewFMP = useCallback(() => {
    if (fmpId) {
      navigate(Paths.FMP.VIEW_FMP.replace(":fmpid", fmpId.toString()))
    }
  }, [navigate, fmpId])

  const projectName = formDataAtom.projectName

  const navigateToMyFMP = useCallback(() => {
    navigate(Paths.FMP.MY_FMP)
  }, [navigate])

  // Every icon except "My FMPs" needs a loaded FMP to act on - view-fmp and
  // edit-fmp always carry an fmpId from the route, but this guards the case
  // where it's missing/not yet resolved.
  const isFmpActive = Boolean(fmpId)
  const editViewLabel = pageType === "edit" ? "View FMP" : "Edit FMP"
  const selectProjectLabel = "Select Project"

  return (
    <Flex alignItems="center" gap="4px">
      <Button variant="Button1RegularWithShadow" onClick={navigateToMyFMP} whiteSpace="nowrap">
        My FMPs
      </Button>

      <ViewActionDivider />

      {pageType === "filter" ? (
        <>
          <Tooltip label={isFmpActive ? "View FMP" : selectProjectLabel} hasArrow fontSize="11px" placement="top">
            <IconButton
              aria-label="View FMP"
              icon={<IconEye size={VIEW_ICON_SIZE} stroke={1.75} />}
              onClick={navigateToViewFMP}
              {...viewIconButtonStyle({ isDisabled: !isFmpActive })}
            />
          </Tooltip>
          <Tooltip label={isFmpActive ? "Edit FMP" : selectProjectLabel} hasArrow fontSize="11px" placement="top">
            <IconButton
              aria-label="Edit FMP"
              icon={<IconEdit size={VIEW_ICON_SIZE} stroke={1.75} />}
              onClick={navigateToEditFMP}
              {...viewIconButtonStyle({ isDisabled: !isFmpActive })}
            />
          </Tooltip>
        </>
      ) : (
        <Tooltip label={isFmpActive ? editViewLabel : selectProjectLabel} hasArrow fontSize="11px" placement="top">
          <IconButton
            aria-label={editViewLabel}
            icon={pageType === "edit" ? <IconEye size={VIEW_ICON_SIZE} stroke={1.75} /> : <IconEdit size={VIEW_ICON_SIZE} stroke={1.75} />}
            onClick={pageType === "edit" ? navigateToViewFMP : navigateToEditFMP}
            {...viewIconButtonStyle({ isDisabled: !isFmpActive })}
          />
        </Tooltip>
      )}

      <ViewActionDivider />

      <Tooltip label={isFmpActive ? "Copy" : selectProjectLabel} hasArrow fontSize="11px" placement="top">
        <Box as="span" display="inline-block">
          <CopyFMP
            fmpId={fmpId}
            projectName={projectName}
            onCopySuccess={() => {}}
            buttonProps={viewIconButtonStyle({ isDisabled: !isFmpActive })}
          >
            <IconCopy size={VIEW_ICON_SIZE} stroke={1.75} />
          </CopyFMP>
        </Box>
      </Tooltip>
      <Tooltip label={isFmpActive ? "PDF Download" : selectProjectLabel} hasArrow fontSize="11px" placement="top">
        <Box as="span" display="inline-block">
          <Menu>
            <MenuButton
              as={Button}
              aria-label="PDF Download"
              isLoading={PDFDownloadLoading}
              p="0"
              {...viewIconButtonStyle({ isDisabled: !isFmpActive })}
            >
              {/* MenuButton always wraps its children in its own flex-stretched
                  span, which left-aligns non-full-width content by default -
                  this re-centers the icon within that span. */}
              <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
                <IconFileTypePdf size={VIEW_ICON_SIZE} stroke={1.75} />
              </Flex>
            </MenuButton>
            <MenuList px="2" borderColor="primary" borderWidth="1px" borderRadius="0" minW="140px">
              <MenuItem
                justifyContent="center"
                borderBottom="1px"
                borderColor="primary"
                color="primary"
                _hover={{ bg: "primary", color: "white" }}
                onClick={() => { handlePDFDownloadClick(0) }}
                fontSize="14px"
              >
                FMP
              </MenuItem>
              <MenuItem
                justifyContent="center"
                borderBottom="1px"
                borderColor="primary"
                color="primary"
                _hover={{ bg: "primary", color: "white" }}
                onClick={() => { handlePDFDownloadClick(1) }}
                fontSize="14px"
              >
                FMP + Reports
              </MenuItem>
              <MenuItem
                justifyContent="center"
                color="primary"
                _hover={{ bg: "primary", color: "white" }}
                onClick={() => { handlePDFDownloadClick(2) }}
                fontSize="14px"
              >
                Reports Only
              </MenuItem>
            </MenuList>
          </Menu>
        </Box>
      </Tooltip>
      <Tooltip label={isFmpActive ? "Excel Report" : selectProjectLabel} hasArrow fontSize="11px" placement="top">
        <IconButton
          aria-label="Excel Report"
          icon={<IconFileSpreadsheet size={VIEW_ICON_SIZE} stroke={1.75} />}
          isLoading={excelDownloadLoading}
          onClick={handleExcelDownload}
          {...viewIconButtonStyle({ isDisabled: !isFmpActive })}
        />
      </Tooltip>

      <ViewActionDivider />

      <Tooltip label={isFmpActive ? "Archive" : selectProjectLabel} hasArrow fontSize="11px" placement="top">
        <Box as="span" display="inline-block">
          <ArchiveOrRestoreFMP
            fmpName={projectName ?? null}
            fmpId={fmpId}
            onArchiveSuccess={navigateToMyFMP}
            actionType="archive"
            buttonProps={viewIconButtonStyle({ isDisabled: !isFmpActive })}
          >
            <IconArchive size={VIEW_ICON_SIZE} stroke={1.75} />
          </ArchiveOrRestoreFMP>
        </Box>
      </Tooltip>
      <Tooltip label={isFmpActive ? "Delete" : selectProjectLabel} hasArrow fontSize="11px" placement="top">
        <Box as="span" display="inline-block">
          <DeleteFMP fmpId={fmpId} onDeleteSuccess={navigateToMyFMP} deleteButtonProps={viewIconButtonStyle({ isDanger: true, isDisabled: !isFmpActive })}>
            <IconTrash size={VIEW_ICON_SIZE} stroke={1.75} />
          </DeleteFMP>
        </Box>
      </Tooltip>
    </Flex>
  )
}
